import { useState } from 'react'
import { Search, X, FileText, MessageSquare, Pencil, Scale, ClipboardList, ShieldOff } from 'lucide-react'
import { format, parseISO } from 'date-fns'
import { useFullTextSearch, type SearchKind, type SearchDateRange, type SearchResult } from '@/hooks/useFullTextSearch'
import type { JournalEntry } from '@/types'
import { cn } from '@/lib/utils'

interface SearchViewProps {
  onOpenEntry: (id: string) => void
  initialQuery?: string
}

const KIND_OPTIONS: { value: SearchKind; label: string }[] = [
  { value: 'all',     label: 'All'     },
  { value: 'journal', label: 'Journal' },
  { value: 'note',    label: 'Notes'   },
]

const DATE_RANGES: { value: SearchDateRange; label: string }[] = [
  { value: 'all',   label: 'Any time'   },
  { value: 'week',  label: 'This week'  },
  { value: 'month', label: 'This month' },
  { value: 'year',  label: 'This year'  },
]

const JOURNAL_TYPE_META: Record<JournalEntry['type'], { label: string | null; Icon: React.ElementType; color: string }> = {
  free:          { label: null,      Icon: Pencil,        color: '' },
  discernment:   { label: 'Discern', Icon: Scale,         color: 'text-indigo-500' },
  weekly_review: { label: 'Review',  Icon: ClipboardList, color: 'text-emerald-600' },
}

const JOURNAL_TYPE_OPTIONS: { value: JournalEntry['type'] | null; label: string }[] = [
  { value: null,           label: 'Any type'  },
  { value: 'free',         label: 'Free'      },
  { value: 'discernment',  label: 'Discern'   },
  { value: 'weekly_review', label: 'Review'   },
]

export function SearchView({ onOpenEntry, initialQuery }: SearchViewProps) {
  const [query, setQuery]               = useState(initialQuery ?? '')
  const [kind, setKind]                 = useState<SearchKind>('all')
  const [dateRange, setDateRange]       = useState<SearchDateRange>('all')
  const [journalType, setJournalType]   = useState<JournalEntry['type'] | null>(null)

  const results  = useFullTextSearch(query, { kind, dateRange, journalType: kind === 'note' ? null : journalType })
  const hasQuery = query.trim().length >= 2

  return (
    <div className="flex flex-col h-full min-h-0 overflow-y-auto">
      <div className="max-w-2xl w-full space-y-4 pb-10">

        {/* Search input */}
        <div className="space-y-1">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <input
              autoFocus
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search notes and journal entries…"
              className="w-full pl-9 pr-9 py-2.5 text-sm bg-muted rounded-lg border border-input focus:outline-none focus:ring-2 focus:ring-ring"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Clear search"
              >
                <X size={14} />
              </button>
            )}
          </div>
          {hasQuery && results !== undefined && (
            <p className="text-xs text-muted-foreground pl-1">
              {results.length === 0
                ? 'No results'
                : `${results.length} result${results.length === 1 ? '' : 's'}`}
            </p>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex gap-0.5 p-0.5 rounded-md bg-muted">
            {KIND_OPTIONS.map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setKind(opt.value)}
                className={cn(
                  'text-xs px-3 py-1 rounded transition-colors',
                  kind === opt.value
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap gap-1">
            {DATE_RANGES.map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setDateRange(opt.value)}
                className={cn(
                  'text-xs px-2 py-0.5 rounded-full border transition-colors',
                  dateRange === opt.value
                    ? 'bg-secondary border-ring text-foreground'
                    : 'border-border text-muted-foreground hover:text-foreground',
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Entry type filter — only when viewing journals */}
          {kind !== 'note' && (
            <div className="flex flex-wrap gap-1">
              {JOURNAL_TYPE_OPTIONS.map(opt => (
                <button
                  key={String(opt.value)}
                  type="button"
                  onClick={() => setJournalType(opt.value)}
                  className={cn(
                    'text-xs px-2 py-0.5 rounded-full border transition-colors',
                    journalType === opt.value
                      ? 'bg-secondary border-ring text-foreground'
                      : 'border-border text-muted-foreground hover:text-foreground',
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Empty state */}
        {!hasQuery && (
          <div className="flex flex-col items-center justify-center py-16 gap-2 text-muted-foreground">
            <Search size={28} className="opacity-20" />
            <p className="text-sm">Search your notes and journal entries.</p>
            <p className="text-xs">Type at least 2 characters to begin.</p>
          </div>
        )}

        {/* Loading */}
        {hasQuery && results === undefined && (
          <p className="text-xs text-muted-foreground px-1">Searching…</p>
        )}

        {/* No results */}
        {hasQuery && results?.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 gap-2 text-muted-foreground">
            <p className="text-sm">No results for "{query}"</p>
            <p className="text-xs">Try a different keyword or widen your filters.</p>
          </div>
        )}

        {/* Results */}
        {hasQuery && results && results.length > 0 && (
          <div className="space-y-2">
            {results.map(result => (
              <ResultCard
                key={`${result.kind}-${result.id}`}
                result={result}
                query={query.trim()}
                onOpen={result.kind === 'journal' ? () => onOpenEntry(result.id) : undefined}
              />
            ))}
          </div>
        )}

      </div>
    </div>
  )
}

// ─── Result card ──────────────────────────────────────────────────────────────

function ResultCard({
  result,
  query,
  onOpen,
}: {
  result: SearchResult
  query: string
  onOpen?: () => void
}) {
  const meta = result.journalType ? JOURNAL_TYPE_META[result.journalType] : null

  const inner = (
    <>
      {/* Header */}
      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
        {result.kind === 'journal' ? (
          <>
            {meta?.Icon
              ? <meta.Icon size={11} className={meta.color || 'text-muted-foreground'} />
              : <FileText size={11} />}
            <span>{meta?.label ?? 'Journal'}</span>
            <span className="text-muted-foreground/40">·</span>
            <span>{result.entryDate ? format(parseISO(result.entryDate), 'MMM d, yyyy') : ''}</span>
            {result.aiExcludable && (
              <span className="ml-auto" title="Excluded from AI">
                <ShieldOff size={10} className="text-muted-foreground/50" />
              </span>
            )}
          </>
        ) : (
          <>
            <MessageSquare size={11} />
            <span>Note</span>
            {result.entityName && (
              <>
                <span className="text-muted-foreground/40">·</span>
                <span className="font-medium text-foreground/70 truncate">{result.entityName}</span>
              </>
            )}
            <span className="ml-auto flex-shrink-0">{format(parseISO(result.createdAt), 'MMM d, yyyy')}</span>
          </>
        )}
      </div>

      {/* Snippet */}
      <p className="text-sm text-muted-foreground leading-relaxed">
        <Highlighted text={result.snippet} query={query} />
      </p>
    </>
  )

  if (onOpen) {
    return (
      <button
        type="button"
        onClick={onOpen}
        className="w-full text-left p-3 rounded-lg border border-border bg-card space-y-1.5 hover:bg-muted/60 transition-colors"
      >
        {inner}
      </button>
    )
  }

  return (
    <div className="p-3 rounded-lg border border-border bg-card space-y-1.5">
      {inner}
    </div>
  )
}

// ─── Keyword highlight ─────────────────────────────────────────────────────────

function Highlighted({ text, query }: { text: string; query: string }) {
  if (!query) return <>{text}</>
  const lower = text.toLowerCase()
  const qLower = query.toLowerCase()
  const idx = lower.indexOf(qLower)
  if (idx === -1) return <>{text}</>
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-amber-200/70 dark:bg-amber-700/50 text-inherit rounded-[2px] px-0.5 not-italic">
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  )
}
